/**
 * The request/error half of the API client, shared by both consoles.
 *
 * The customer dashboard and the admin console are separate bundles with
 * separate logins and separate token keys (B2B D4) — but the same server, the
 * same error shapes and the same "your session lapsed" handling. Only the base
 * path and the storage key differ, so those are what `createClient` takes.
 */

/** App uses this to tell a lapsed session apart from a rejected login. */
export const SESSION_EXPIRED = "Session expired";

/**
 * Coded 403s that refuse the *account* rather than one request.
 *
 * Retrying cannot help and neither can any other page, so a session that meets
 * one of these ends the way a 401 does — otherwise the customer is left on a
 * dashboard that answers every panel with a red box and never says why.
 */
export const ACCOUNT_CLOSED = new Set(["tenant_expired", "tenant_suspended", "console_blocked"]);

/** A validation failure the server describes issue by issue. */
export interface ValidationDetail {
  message: string;
  errors: Array<{ level: string; code: string; message: string; path: string }>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Present when the server sent a structured detail rather than a string. */
    readonly issues?: ValidationDetail["errors"],
    /**
     * A machine-readable reason, when the server sent one: `tenant_expired`,
     * `tenant_suspended`, `too_many_attempts`, `build_not_ready`… The message
     * is for reading, this is for deciding what to show.
     */
    readonly code?: string,
    /** Set alongside `code === "tenant_expired"`. */
    readonly expiresAt?: string,
    /**
     * The whole coded detail, for the handful of codes that carry more than a
     * code — `design_unsupported` names the release, for instance, and the
     * wording is useless without it.
     */
    readonly detail?: Record<string, unknown>,
  ) {
    super(message);
  }
}

function isValidationDetail(detail: unknown): detail is ValidationDetail {
  return (
    typeof detail === "object" &&
    detail !== null &&
    Array.isArray((detail as ValidationDetail).errors)
  );
}

function isCodedDetail(detail: unknown): detail is { code: string; expires_at?: string } {
  return (
    typeof detail === "object" &&
    detail !== null &&
    typeof (detail as { code?: unknown }).code === "string"
  );
}

export interface TokenStore {
  get(): string | null;
  set(token: string | null): void;
}

/**
 * Set on a tab that was *handed* its session instead of signing in — the
 * customer console our admin console opens (`api/handoff.ts`, B2B §7.1).
 *
 * Such a tab keeps its session to itself. Two customers opened from the
 * console are two tabs, and a token in localStorage is one drawer they would
 * both write to: the second click would sign the first tab in as somebody
 * else, silently, in a window still showing the first customer's name.
 * sessionStorage is per tab, survives a reload and dies with the tab, which is
 * exactly the life a support session should have.
 */
const HANDED_SESSION = "qa.handed-session";

/** Called once, before the handed token is stored. */
export function markHandedSession(): void {
  try {
    sessionStorage.setItem(HANDED_SESSION, "1");
  } catch {
    /* private browsing — the session lives in memory for this page load */
  }
}

/** Whether this tab is one of those. Also what puts the support strip on the
 *  screen: a dashboard opened as somebody else must never look like our own. */
export function isHandedSession(): boolean {
  try {
    return sessionStorage.getItem(HANDED_SESSION) !== null;
  } catch {
    return false;
  }
}

/** Where this tab keeps its session: its own storage when it was handed one,
 *  the browser's otherwise. Null when neither can be reached at all. */
function sessionStore(): Storage | null {
  try {
    return isHandedSession() ? sessionStorage : localStorage;
  } catch {
    return null;
  }
}

/** The browser's storage, with private-browsing failures swallowed. */
export function tokenStore(key: string): TokenStore {
  return {
    get() {
      try {
        return sessionStore()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    set(token) {
      try {
        const store = sessionStore();
        if (!store) return;
        if (token) store.setItem(key, token);
        else store.removeItem(key);
      } catch {
        /* private browsing — the session simply will not survive a reload */
      }
    },
  };
}

export interface HttpClient {
  request<T>(path: string, init?: RequestInit): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  tokens: TokenStore;
  base: string;
}

export function createClient({
  base,
  tokenKey,
  onSessionEnded,
}: {
  base: string;
  tokenKey: string;
  /** Called once when the server closes the account under an open session. */
  onSessionEnded?: (error: ApiError) => void;
}): HttpClient {
  const tokens = tokenStore(tokenKey);

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = tokens.get();
    const isFormData = init.body instanceof FormData;
    const response = await fetch(`${base}${path}`, {
      ...init,
      headers: {
        // FormData sets its own multipart boundary; naming a type here would
        // produce a body the server cannot parse.
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });

    if (!response.ok) {
      let detail = response.statusText;
      let issues: ValidationDetail["errors"] | undefined;
      let code: string | undefined;
      let expiresAt: string | undefined;
      let coded: Record<string, unknown> | undefined;
      try {
        const body = await response.json();
        if (typeof body.detail === "string") {
          detail = body.detail;
        } else if (isCodedDetail(body.detail)) {
          code = body.detail.code;
          expiresAt = body.detail.expires_at;
          coded = body.detail as Record<string, unknown>;
          // The caller decides the wording from `code`; the raw code stands in
          // for anything that just prints the message.
          detail = body.detail.code;
          // A coded refusal may *also* carry issues — `design_module_disabled`
          // names both the module and every block that uses it (MODULES §8.4),
          // and losing one half of that to the shape test would leave the page
          // with nothing to point at.
          if (isValidationDetail(body.detail)) issues = body.detail.errors;
        } else if (isValidationDetail(body.detail)) {
          // Keep the issues structured so the UI can list them instead of
          // dumping JSON at whoever is reading the screen.
          detail = body.detail.message;
          issues = body.detail.errors;
        } else if (body.detail !== undefined) {
          detail = JSON.stringify(body.detail);
        }
      } catch {
        /* keep statusText */
      }
      if (response.status === 401) {
        // Only a request we sent credentials with can have had them expire; a
        // 401 from signing in just means the password was wrong.
        const hadToken = token !== null;
        tokens.set(null);
        // The code travels with it: a 401 is normally "wrong password", which
        // has none, but the console handoff answers one with `handoff_expired`
        // and the screen it lands on has a sentence for that.
        throw new ApiError(401, hadToken ? SESSION_EXPIRED : detail, issues, code);
      }
      const failure = new ApiError(response.status, detail, issues, code, expiresAt, coded);
      if (response.status === 403 && code && ACCOUNT_CLOSED.has(code) && token !== null) {
        // The account itself is closed. Drop the token so the app falls back
        // to the login screen, and hand the refusal on so that screen can say
        // which of the three it was, in the customer's own words.
        tokens.set(null);
        onSessionEnded?.(failure);
      }
      throw failure;
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  return {
    request,
    post: <T,>(path: string, body?: unknown) =>
      request<T>(path, {
        method: "POST",
        body:
          body === undefined
            ? undefined
            : body instanceof FormData
              ? body
              : JSON.stringify(body),
      }),
    tokens,
    base,
  };
}
